import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { IonLoading, IonicSafeString, useIonAlert } from '@ionic/react'
import { useDebounceEffect } from 'ahooks'
import { compact, uniq } from 'lodash'
import { type PropsWithChildren, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CAPGO_CHANNEL } from '../data/environment'
import { ensureDeviceId } from '../helpers/ensureDeviceId'

/**
 * Wraps a tap target (e.g. the footer logo). On native, 5 taps within 800ms
 * reveals an alert with the device id, Capgo channel, bundle/native version and
 * live-update id — handy for verifying which OTA bundle/channel is running — plus
 * a "Reset App" action. No-op on web. Ported from customer-app-v2 (no Sentry).
 */
export const DebugHandler = ({ children }: PropsWithChildren) => {
  if (Capacitor.isNativePlatform()) {
    return <DebugHandlerNative>{children}</DebugHandlerNative>
  }
  return <>{children}</>
}

const DebugHandlerNative = ({ children }: PropsWithChildren) => {
  const history = useHistory()
  const [count, setCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const onClick = () => setCount((c) => c + 1)
  const [presentAlert] = useIonAlert()

  useDebounceEffect(
    () => {
      setCount(0)
    },
    [count],
    { wait: 800 },
  )

  const showDebugInfo = async () => {
    const deviceId = ensureDeviceId()
    const { deviceId: liveUpdateId } = await CapacitorUpdater.getDeviceId()
    const { channel } = await CapacitorUpdater.getChannel()
    const { bundle, native } = await CapacitorUpdater.current()

    presentAlert({
      header: 'Debug Information',
      message: new IonicSafeString(
        [
          '<strong>Capgo OTA Test</strong>',
          'This is a test of Capgo in v2',
          '<strong>Device ID</strong>',
          deviceId,
          '<strong>Bundle ID</strong>',
          bundle.checksum || 'native',
          '<strong>Bundle Version</strong>',
          bundle.version,
          '<strong>Native Version</strong>',
          native,
          '<strong>Channel</strong>',
          uniq(compact([CAPGO_CHANNEL, channel])).join(' | '),
          '<strong>Update ID</strong>',
          liveUpdateId,
        ].join('<br />'),
      ),
      buttons: [
        {
          role: 'cancel',
          text: 'Close',
        },
        {
          text: 'Reset App',
          role: 'destructive',
          handler: async () => {
            setTimeout(() => {
              CapacitorUpdater.reset({
                toLastSuccessful: false,
              })
            }, 0)
            history.push('/logout')
          },
        },
      ],
    })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only on tap count
  useEffect(() => {
    if (count >= 5) {
      ;(async () => {
        setIsLoading(true)
        await showDebugInfo()
        setIsLoading(false)
      })()
    }
  }, [count])

  return (
    <button
      type='button'
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'default',
      }}
    >
      <IonLoading isOpen={isLoading} />
      {children}
    </button>
  )
}
